import { CategoryWifiProfileConfiguration } from '@era/settings-sdk';
import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { Category } from '../router/category';
import * as fromCategoryWifiProfiles from './category-wifi-profile-preset-configuration.reducer';
import { CategoryWifiProfilePresetConfigurationState } from './category-wifi-profile-preset-configuration.state';

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectCategoryWifiProfilePresetConfigurationState =
  createFeatureSelector<CategoryWifiProfilePresetConfigurationState>('categoryWifiProfilePresetConfiguration');

export const selectAllCategoryWifiProfilePresetConfigurations = createSelector(
  selectCategoryWifiProfilePresetConfigurationState,
  fromCategoryWifiProfiles.selectAllCategoryWifiProfilePresetConfigurations
);

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

// Generic selector factory functions that work with any CategoryType
// These eliminate the need to replicate selectors for every category type

export const createSelectWifiProfilePresetConfigurationsLinkedToCategory = (category: Category) => {
  return createSelector(
    selectAllCategoryWifiProfilePresetConfigurations,
    (wifiProfiles: CategoryWifiProfileConfiguration[]) => {
      if (!category) {
        return [];
      }

      return wifiProfiles.filter(
        (wifiProfile: CategoryWifiProfileConfiguration) =>
          wifiProfile.categoryId === category.categoryId && wifiProfile.categoryType === category.categoryType
      );
    }
  );
};

export const createSelectWifiProfilePresetConfigurationsLinkedToCategoryLoaded = (category: Category) => {
  return createSelector(
    createSelectWifiProfilePresetConfigurationsLinkedToCategory(category),
    (wifiProfiles: CategoryWifiProfileConfiguration[]) => {
      if (!category) {
        return false;
      }
      return wifiProfiles && wifiProfiles.length > 0;
    }
  );
};

export const createSelectDistinctWifiProfilePresetConfigurationsLinkedToCategory = (category: Category) => {
  return createSelector(
    createSelectWifiProfilePresetConfigurationsLinkedToCategory(category),
    (wifiProfiles: CategoryWifiProfileConfiguration[]) =>
      Array.from(new Map(wifiProfiles.map((profile) => [profile.wifiProfileId, profile])).values())
  );
};

// ============================================================================
// GENERIC SELECTORS - One-liner selectors for containers
// ============================================================================

/**
 * Generic selector for wifi profile preset configurations applied to any category type (with inheritance).
 * Automatically handles inheritance logic.
 * Deduplicates profiles by profileId (if a profile exists at multiple levels, the most specific one is kept).
 *
 * Usage in containers:
 * this.inheritedWifiProfiles$ = this.store.select(selectWifiProfilePresetConfigurationsAppliedToCategory(category));
 */
export const selectWifiProfilePresetConfigurationsAppliedToCategory = (
  category: Category
): MemoizedSelector<object, CategoryWifiProfileConfiguration[]> => {
  if (category.parent) {
    return createSelector(
      selectWifiProfilePresetConfigurationsAppliedToCategory(category.parent),
      createSelectWifiProfilePresetConfigurationsLinkedToCategory(category),
      (parent, current) => Array.from(new Map([...parent, ...current].map((i) => [i.wifiProfileId, i])).values())
    );
  } else {
    return createSelectWifiProfilePresetConfigurationsLinkedToCategory(category);
  }
};
